import type {
  QuoterConfig,
  QuoterModel,
  QuoterPriceList,
  QuoterScheme,
} from "../types";

/**
 * Almikia Epic EQ2 (TASK-099): pure form rules of the Direccion configuration
 * screens. Forms keep every value as a string (what the user typed); the
 * validators return an error KEY (resolved under
 * `crm.quoter.config.errors.*`), never a message, so the screens stay
 * translatable and these rules stay testable without a DOM.
 */

export type RuleErrors<Field extends string> = Partial<Record<Field, string>>;

/** Parses a typed number ("1 200,5" and "1200.5" both work); null when empty or invalid. */
export const parseNumber = (raw: string): number | null => {
  const cleaned = raw.trim().replace(/\s/g, "").replace(",", ".");
  if (cleaned === "") return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
};

const isWholeNumber = (value: number | null, min: number, max = Infinity) =>
  value != null && Number.isInteger(value) && value >= min && value <= max;

const asText = (value: number | null | undefined) =>
  value == null ? "" : String(value);

/** The installments term bounds of `quoter_apply_term()` (1 to 36 months). */
export const MIN_TERM_MONTHS = 1;
export const MAX_TERM_MONTHS = 36;

// -- Project configuration -------------------------------------------------

export const CONFIG_NUMBER_FIELDS = [
  "apartado_amount",
  "apartado_days",
  "quote_validity_days",
  "term_months",
  "excess_price_per_m2",
  "client_protection_days",
] as const;
export type ConfigNumberField = (typeof CONFIG_NUMBER_FIELDS)[number];
export type ConfigField = ConfigNumberField | "released_stages";

export type ConfigFormValues = Record<ConfigField, string> & {
  apartados_count_toward_list: boolean;
};

export const configToFormValues = (config: QuoterConfig): ConfigFormValues => ({
  apartado_amount: asText(config.apartado_amount),
  apartado_days: asText(config.apartado_days),
  quote_validity_days: asText(config.quote_validity_days),
  term_months: asText(config.term_months),
  excess_price_per_m2: asText(config.excess_price_per_m2),
  client_protection_days: asText(config.client_protection_days),
  released_stages: config.released_stages.join(", "),
  apartados_count_toward_list: config.apartados_count_toward_list,
});

/** "1, 2, 3" -> [1, 2, 3]; null when a part is not a positive whole number. */
export const parseStages = (raw: string): number[] | null => {
  const parts = raw
    .split(/[,\s]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  const stages = parts.map(Number);
  if (!stages.every((stage) => Number.isInteger(stage) && stage >= 1)) {
    return null;
  }
  return [...new Set(stages)].sort((a, b) => a - b);
};

export const validateConfigForm = (
  values: ConfigFormValues,
): RuleErrors<ConfigField> => {
  const errors: RuleErrors<ConfigField> = {};
  const amount = (field: "apartado_amount" | "excess_price_per_m2") => {
    const value = parseNumber(values[field]);
    if (value == null || value < 0) errors[field] = "amount_invalid";
  };
  amount("apartado_amount");
  amount("excess_price_per_m2");
  for (const field of [
    "apartado_days",
    "quote_validity_days",
    "client_protection_days",
  ] as const) {
    if (!isWholeNumber(parseNumber(values[field]), 0)) {
      errors[field] = "days_invalid";
    }
  }
  if (
    !isWholeNumber(
      parseNumber(values.term_months),
      MIN_TERM_MONTHS,
      MAX_TERM_MONTHS,
    )
  ) {
    errors.term_months = "term_invalid";
  }
  if (parseStages(values.released_stages) == null) {
    errors.released_stages = "stages_invalid";
  }
  return errors;
};

/**
 * The editable columns of `quoter_configs` from VALID form values, WITHOUT the
 * installments term: that one only changes through `quoterApplyTerm` after the
 * "ver impacto" confirmation.
 */
export const toConfigData = (values: ConfigFormValues) => ({
  apartado_amount: parseNumber(values.apartado_amount) ?? 0,
  apartado_days: parseNumber(values.apartado_days) ?? 0,
  quote_validity_days: parseNumber(values.quote_validity_days) ?? 0,
  excess_price_per_m2: parseNumber(values.excess_price_per_m2) ?? 0,
  client_protection_days: parseNumber(values.client_protection_days) ?? 0,
  released_stages: parseStages(values.released_stages) ?? [],
  apartados_count_toward_list: values.apartados_count_toward_list,
});

// -- Price lists -------------------------------------------------------------

/** What the user is editing on ONE price list row. */
export type PriceListDraft = {
  block_size: string;
  increment_pct: string;
  /** Captured price per model id (string key), empty = not captured. */
  captured: Record<string, string>;
};

export const priceListToDraft = (
  list: QuoterPriceList,
  captured: Record<string, string>,
): PriceListDraft => ({
  block_size: asText(list.block_size),
  increment_pct: asText(list.increment_pct),
  captured,
});

export type PriceListErrors = {
  block_size?: string;
  increment_pct?: string;
  captured: Record<string, string>;
};

/** List 1 has no increment (its price is the base price). */
export const validatePriceListDraft = (
  draft: PriceListDraft,
  listNumber: number,
): PriceListErrors => {
  const errors: PriceListErrors = { captured: {} };
  if (!isWholeNumber(parseNumber(draft.block_size), 1)) {
    errors.block_size = "block_invalid";
  }
  if (listNumber > 1 && draft.increment_pct.trim() !== "") {
    const pct = parseNumber(draft.increment_pct);
    if (pct == null || pct < 0 || pct > 100) {
      errors.increment_pct = "pct_invalid";
    }
  }
  for (const [modelId, raw] of Object.entries(draft.captured)) {
    if (raw.trim() === "") continue;
    const price = parseNumber(raw);
    if (price == null || price <= 0) errors.captured[modelId] = "price_invalid";
  }
  return errors;
};

export const hasPriceListErrors = (errors: PriceListErrors) =>
  errors.block_size != null ||
  errors.increment_pct != null ||
  Object.keys(errors.captured).length > 0;

// -- Models ------------------------------------------------------------------

export const MODEL_FIELDS = [
  "code",
  "name",
  "base_price",
  "roof_price",
  "m2_per_floor",
  "total_construction",
  "program",
  "garage",
] as const;
export type ModelField = (typeof MODEL_FIELDS)[number];
export type ModelFormValues = Record<ModelField, string>;

export const EMPTY_MODEL_FORM: ModelFormValues = {
  code: "",
  name: "",
  base_price: "",
  roof_price: "0",
  m2_per_floor: "",
  total_construction: "",
  program: "",
  garage: "",
};

export const modelToFormValues = (model: QuoterModel): ModelFormValues => ({
  code: model.code,
  name: model.name,
  base_price: asText(model.base_price),
  roof_price: asText(model.roof_price),
  m2_per_floor: asText(model.m2_per_floor),
  total_construction: asText(model.total_construction),
  program: model.program ?? "",
  garage: model.garage ?? "",
});

export const validateModelForm = (
  values: ModelFormValues,
  isNew: boolean,
): RuleErrors<ModelField> => {
  const errors: RuleErrors<ModelField> = {};
  if (isNew && !/^[A-Za-z0-9_-]+$/.test(values.code.trim())) {
    errors.code = "code_invalid";
  }
  if (values.name.trim() === "") errors.name = "name_required";
  const base = parseNumber(values.base_price);
  if (base == null || base <= 0) errors.base_price = "price_invalid";
  const roof = parseNumber(values.roof_price);
  if (roof == null || roof < 0) errors.roof_price = "amount_invalid";
  for (const field of ["m2_per_floor", "total_construction"] as const) {
    if (values[field].trim() === "") continue;
    const value = parseNumber(values[field]);
    if (value == null || value < 0) errors[field] = "amount_invalid";
  }
  return errors;
};

/** Column values of a VALID model form (optional blanks become null). */
export const toModelData = (values: ModelFormValues) => ({
  name: values.name.trim(),
  base_price: parseNumber(values.base_price) ?? 0,
  roof_price: parseNumber(values.roof_price) ?? 0,
  m2_per_floor: parseNumber(values.m2_per_floor),
  total_construction: parseNumber(values.total_construction),
  program: values.program.trim() || null,
  garage: values.garage.trim() || null,
});

// -- Payment schemes ---------------------------------------------------------

export const SCHEME_FIELDS = [
  "name",
  "down_pct",
  "monthly_pct",
  "deed_pct",
  "discount_pct",
] as const;
export type SchemeField = (typeof SCHEME_FIELDS)[number];
export type SchemeFormValues = Record<SchemeField, string>;
/** `sum` is the error of the three percentages together. */
export type SchemeErrors = RuleErrors<SchemeField | "sum">;

export const EMPTY_SCHEME_FORM: SchemeFormValues = {
  name: "",
  down_pct: "",
  monthly_pct: "",
  deed_pct: "",
  discount_pct: "0",
};

export const schemeToFormValues = (scheme: QuoterScheme): SchemeFormValues => ({
  name: scheme.name,
  down_pct: asText(scheme.down_pct),
  monthly_pct: asText(scheme.monthly_pct),
  deed_pct: asText(scheme.deed_pct),
  discount_pct: asText(scheme.discount_pct),
});

/** Down + monthly + deed must be exactly 100; the discount is 0 to 99. */
export const validateSchemeForm = (values: SchemeFormValues): SchemeErrors => {
  const errors: SchemeErrors = {};
  if (values.name.trim() === "") errors.name = "name_required";
  const parts = (["down_pct", "monthly_pct", "deed_pct"] as const).map(
    (field) => {
      const pct = parseNumber(values[field]);
      if (pct == null || pct < 0 || pct > 100) errors[field] = "pct_invalid";
      return pct;
    },
  );
  if (parts.every((pct) => pct != null)) {
    const sum = Math.round(
      parts.reduce<number>((a, b) => a + (b ?? 0), 0) * 1e6,
    );
    if (sum !== 100 * 1e6) errors.sum = "sum_not_100";
  }
  const discount = parseNumber(values.discount_pct);
  if (discount == null || discount < 0 || discount >= 100) {
    errors.discount_pct = "discount_invalid";
  }
  return errors;
};

export const toSchemeData = (values: SchemeFormValues) => ({
  name: values.name.trim(),
  down_pct: parseNumber(values.down_pct) ?? 0,
  monthly_pct: parseNumber(values.monthly_pct) ?? 0,
  deed_pct: parseNumber(values.deed_pct) ?? 0,
  discount_pct: parseNumber(values.discount_pct) ?? 0,
});
